import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import type { SSEEvent } from './lib/agents/types.js';

const ChatInterface = lazy(async () => {
  const module = await import('./components/chat/ChatInterface.js');
  return { default: module.ChatInterface };
});

const AnalysisSidebar = lazy(async () => {
  const module = await import('./components/sidebar/AnalysisSidebar.js');
  return { default: module.AnalysisSidebar };
});

const Sidebar = lazy(async () => {
  const module = await import('./components/sidebar/Sidebar.js');
  return { default: module.Sidebar };
});

interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  imageUrl: string | null;
}

function getDisplayName(user: CurrentUser): string {
  if (user.name && user.name.trim()) return user.name;
  return user.email.split('@')[0] || 'Clinician';
}

function ShieldIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2 4 5.5V12c0 5.2 3.4 9.9 8 11.5 4.6-1.6 8-6.3 8-11.5V5.5L12 2Z" stroke="currentColor" strokeWidth="1.6" />
      <path d="m8.5 12 2.3 2.3 4.7-4.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.4 5.4 2.4 13.2l7.9 6.1C12.1 13.6 17.5 9.5 24 9.5Z" />
      <path fill="#4285F4" d="M46.9 24.5c0-1.6-.1-2.8-.4-4.1H24v8h13.2c-.3 2-1.7 5-4.8 7l7.4 5.7c4.5-4.1 7.1-10.2 7.1-16.6Z" />
      <path fill="#FBBC05" d="M10.3 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.3.8-4.7L2.4 13.2C.9 16.2 0 20 0 24s.9 7.8 2.4 10.8l7.9-6.1Z" />
      <path fill="#34A853" d="M24 48c6.5 0 12-2.1 16-5.8l-7.4-5.7c-2 1.4-4.7 2.5-8.6 2.5-6.5 0-11.9-4.1-13.8-9.8l-7.9 6.1C6.4 42.6 14.6 48 24 48Z" />
    </svg>
  );
}

export function App() {
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [authNotice, setAuthNotice] = useState<{ type: 'pending' | 'oauth_error'; email: string | null } | null>(null);
  const [events, setEvents] = useState<SSEEvent[]>([]);
  const [focusChat, setFocusChat] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState('');
  const [newSessionKey, setNewSessionKey] = useState(0);
  const chatGenerationRef = useRef(0);
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window === 'undefined' ? 1280 : window.innerWidth);
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerTriggerRef = useRef<HTMLElement | null>(null);
  const leftDrawer = viewportWidth < 768 && leftOpen && !focusChat;
  const rightDrawer = viewportWidth < 1024 && rightOpen && !focusChat;
  const drawerOpen = leftDrawer || rightDrawer;

  useEffect(() => {
    if (!drawerOpen || typeof document === 'undefined') return;
    const previousFocus = drawerTriggerRef.current ?? document.activeElement as HTMLElement | null;
    const triggerLabel = previousFocus?.getAttribute('aria-label');
    const panel = drawerRef.current;
    const focusable = () => Array.from(panel?.querySelectorAll<HTMLElement>('button, a[href], select, input, textarea, [tabindex="0"]') ?? []).filter(element => !element.hasAttribute('disabled'));
    panel?.focus();
    focusable()[0]?.focus();
    const observer = new MutationObserver(() => {
      if (document.activeElement === panel || !panel?.contains(document.activeElement)) focusable()[0]?.focus();
    });
    if (panel) observer.observe(panel, { childList: true, subtree: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setLeftOpen(false); setRightOpen(false); }
      if (event.key !== 'Tab') return;
      const elements = focusable(), first = elements[0], last = elements[elements.length - 1];
      if (!panel?.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first)?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      observer.disconnect(); document.removeEventListener('keydown', onKey);
      // Restore after React has removed inert from the main content.
      requestAnimationFrame(() => {
        const trigger = previousFocus?.isConnected ? previousFocus
          : triggerLabel ? document.querySelector<HTMLElement>(`[aria-label="${CSS.escape(triggerLabel)}"]`) : null;
        trigger?.focus();
      });
    };
  }, [drawerOpen, leftDrawer, rightDrawer]);

  const applyResponsiveSidebarRules = (width: number, nextLeft: boolean, nextRight: boolean) => {
    if (width < 768) return { left: false, right: false };
    if (width < 1024) return { left: nextLeft, right: false };
    return { left: nextLeft, right: nextRight };
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const leftStored = window.localStorage.getItem('deepmedsp_left_open');
    const rightStored = window.localStorage.getItem('deepmedsp_right_open');
    const baseLeft = leftStored === null ? true : leftStored === 'true';
    const baseRight = rightStored === null ? false : rightStored === 'true';
    const resolved = applyResponsiveSidebarRules(window.innerWidth, baseLeft, baseRight);
    setLeftOpen(resolved.left);
    setRightOpen(resolved.right);

    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setLeftOpen((prev) => (window.innerWidth < 768 ? false : prev));
      setRightOpen((prev) => (window.innerWidth < 1024 ? false : prev));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('deepmedsp_left_open', String(leftOpen));
    }
  }, [leftOpen]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('deepmedsp_right_open', String(rightOpen));
    }
  }, [rightOpen]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authState = params.get('auth');
    if (authState !== 'pending' && authState !== 'oauth_error') {
      setAuthNotice(null);
      return;
    }
    setAuthNotice({ type: authState, email: params.get('email') });
  }, []);

  useEffect(() => {
    const loadMe = async () => {
      try {
        const res = await fetch('/api/auth/me');
        if (!res.ok) {
          setCurrentUser(null);
          return;
        }
        const data = (await res.json()) as { authenticated: boolean; user?: CurrentUser };
        const nextUser = data.authenticated ? (data.user ?? null) : null;
        setCurrentUser(nextUser);

        if (nextUser) {
          const url = new URL(window.location.href);
          url.searchParams.delete('auth');
          url.searchParams.delete('email');
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          setAuthNotice(null);
        }
      } catch {
        setCurrentUser(null);
      } finally {
        setAuthLoading(false);
      }
    };

    void loadMe();
  }, []);

  useEffect(() => {
    setActiveSessionId('');
  }, [currentUser?.id]);

  const logout = async () => {
    try {
      const res = await fetch('/api/auth/logout', { method: 'POST' });
      if (!res.ok) throw new Error('Không thể đăng xuất. Vui lòng thử lại.');
      setCurrentUser(null);
      setActiveSessionId('');
      setEvents([]);
    } catch {
      window.alert('Không thể đăng xuất. Phiên đăng nhập vẫn đang hoạt động; vui lòng thử lại.');
    }
  };

  const deleteSession = async (sessionId: string) => {
    const confirmed = window.confirm('Delete this session permanently?');
    if (!confirmed) return;

    try {
      const res = await fetch('/api/sessions', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });

      if (!res.ok) {
        const details = await res.text();
        throw new Error(details || 'Delete session failed');
      }

      setActiveSessionId((current) => current === sessionId ? '' : current);
      setSidebarRefreshKey((prev) => prev + 1);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Delete session failed';
      window.alert(msg);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(160deg,#f9f5f5_0%,#f4edec_100%)] px-4">
        <div className="w-full max-w-[720px] overflow-hidden rounded-3xl border border-[#e7dcda] bg-white shadow-[0_24px_58px_rgba(122,22,28,0.14)]">
          <div className="border-b border-[#7f171a] bg-[linear-gradient(120deg,#a2252a_0%,#8c1b20_55%,#711417_100%)] px-7 py-7 text-white md:px-9">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[rgba(255,255,255,0.62)]">BỆNH VIỆN ĐA KHOA XANH PÔN</p>
            <h1 className="mt-2 text-[24px] font-semibold">DeepMedSP-AI</h1>
          </div>
          <div className="px-7 py-10 text-center md:px-9">
            <div className="mx-auto mb-4 h-10 w-10 animate-pulse-soft rounded-full border border-[#e7dcda] bg-[#fff9f9]" />
            <p className="text-sm font-medium text-[#1a1a1a]">Đang kiểm tra phiên đăng nhập...</p>
            <p className="mt-1 text-xs text-[#64748b]">Hệ thống đang xác thực tài khoản và dữ liệu phiên.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(160deg,#f9f5f5_0%,#f4edec_100%)] px-3 py-5 sm:px-4 sm:py-6">
        <div className="w-full max-w-[860px] overflow-hidden rounded-3xl border border-[#e7dcda] bg-white shadow-[0_24px_58px_rgba(122,22,28,0.14)]">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="flex flex-col bg-[linear-gradient(140deg,#a2252a_0%,#8c1b20_58%,#711417_100%)] px-6 py-8 text-white md:px-8 md:py-10">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[rgba(255,255,255,0.62)]">BỆNH VIỆN ĐA KHOA XANH PÔN</p>
              <h1 className="mt-3 text-[28px] font-bold leading-[1.2] text-white">DeepMedSP-AI</h1>
              <p className="mt-4 text-[14px] leading-[1.65] text-[rgba(255,255,255,0.8)]">
                Hỗ trợ tra cứu và học tập về sử dụng kháng sinh cho dược sĩ lâm sàng
              </p>

              <ul className="mt-6 space-y-2.5 text-[13px] leading-relaxed text-[rgba(255,255,255,0.78)]">
                <li className="flex items-start gap-2.5">
                  <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-[rgba(255,255,255,0.72)]" />
                  <span>Tính liều theo chức năng thận, cân nặng</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-[rgba(255,255,255,0.72)]" />
                  <span>Kiểm tra tương tác và chống chỉ định</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-[rgba(255,255,255,0.72)]" />
                  <span>Tra cứu phác đồ & bằng chứng lâm sàng</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-[rgba(255,255,255,0.72)]" />
                  <span>Lưu lịch sử hội đáp theo tài khoản</span>
                </li>
              </ul>

              <div className="mt-8 inline-flex w-fit items-center gap-1.5 rounded-full border border-white/35 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[rgba(255,255,255,0.72)] md:mt-auto">
                <ShieldIcon />
                <span>Secure Google Sign-in</span>
              </div>
            </div>

            <div className="flex flex-col bg-white px-6 py-8 md:px-8 md:py-10">
              <h2 className="text-[22px] font-semibold text-[#1a1a1a]">
                {authNotice?.type === 'pending' ? 'Tài khoản đang chờ xét duyệt' : 'Đăng nhập để tiếp tục'}
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-[#64748b]">
                {authNotice?.type === 'pending'
                  ? 'Tài khoản Google đã được ghi nhận trong hệ thống. Vui lòng chờ quản trị viên duyệt trong cơ sở dữ liệu trước khi đăng nhập.'
                  : 'Sử dụng tài khoản Google bệnh viện để quản lý session riêng và theo dõi lịch sử hội đáp.'}
              </p>

              {authNotice?.type === 'pending' ? (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800">
                  <p className="font-semibold">Trạng thái: Chờ duyệt</p>
                  <p className="mt-1">{authNotice.email ? `Email đăng ký: ${authNotice.email}` : 'Vui lòng liên hệ quản trị để được duyệt tài khoản.'}</p>
                  <p className="mt-1">Sau khi được duyệt, bạn có thể đăng nhập lại bằng Google.</p>
                </div>
              ) : null}

              {authNotice?.type === 'oauth_error' ? (
                <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12px] text-rose-700">
                  Đăng nhập Google chưa thành công. Vui lòng thử lại.
                </div>
              ) : null}

              <a
                href="/api/auth/google"
                aria-label="Tiếp tục đăng nhập bằng Google"
                className="mt-6 inline-flex min-h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-[#e7dcda] bg-white px-5 py-3 text-[14px] font-medium text-[#1a1a1a] transition hover:border-[#d8cbc9] hover:bg-[#faf6f5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9f1d23] focus-visible:ring-offset-2"
              >
                <GoogleIcon />
                <span>{authNotice?.type === 'pending' ? 'Đăng nhập lại với Google' : 'Tiếp tục với Google'}</span>
              </a>

              <div className="mt-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-[#eee5e3]" />
                <span className="text-[11px] text-[#aaa4a1]">chỉ dành cho nhân viên bệnh viện</span>
                <div className="h-px flex-1 bg-[#eee5e3]" />
              </div>

              <p className="mt-4 text-[12px] leading-relaxed text-[#7b7672]">
                Đăng nhập đồng nghĩa bạn đồng ý với quy định bảo mật dữ liệu nội bộ của bệnh viện.
              </p>

              <div className="mt-auto border-t border-[#f0e9e7] pt-4 text-center text-[11px] text-[#aaa4a1]">
                Sản phẩm của Khoa Dược · Bệnh viện Đa khoa Xanh Pôn
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b border-[var(--line)] bg-[var(--card)] px-3 py-2 md:px-4 xl:hidden">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-[var(--ink)]" title={getDisplayName(currentUser)}>{getDisplayName(currentUser)}</p>
          <p className="truncate text-[11px] text-[var(--muted)]" title={currentUser.email}>{currentUser.email}</p>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          className="rounded-lg border border-[var(--line)] bg-white px-2.5 py-1 text-[10px] font-semibold text-[var(--ink)] transition hover:bg-[#faf6f5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2"
        >
          Đăng xuất
        </button>
      </div>

      {drawerOpen ? <button type="button" tabIndex={-1} aria-label="Đóng thanh bên" onClick={() => { setLeftOpen(false); setRightOpen(false); }}
        className="fixed inset-0 z-40 bg-black/40" /> : null}
      <div className="flex min-h-0 w-full flex-1 items-stretch p-0 m-0 gap-0">
        <div
          ref={leftDrawer ? drawerRef : undefined}
          tabIndex={leftDrawer ? -1 : undefined}
          role={leftDrawer ? 'dialog' : undefined}
          aria-modal={leftDrawer ? true : undefined}
          aria-label={leftDrawer ? 'Danh sách hội thoại' : undefined}
          className={leftDrawer ? 'fixed inset-y-0 left-0 z-50 bg-white shadow-xl' : 'relative h-full flex-shrink-0'}
          style={{
            width: !focusChat && leftOpen ? leftDrawer ? Math.min(320, viewportWidth * 0.85) : 280 : 0,
            opacity: !focusChat && leftOpen ? 1 : 0,
            overflow: !focusChat && leftOpen ? 'auto' : 'hidden',
            pointerEvents: !focusChat && leftOpen ? 'auto' : 'none',
            borderRight: !focusChat && leftOpen ? '0.5px solid var(--line)' : '0',
            transition: 'width 220ms ease, opacity 180ms ease',
          }}
          aria-hidden={!leftOpen || focusChat}
        >
          {!focusChat && leftOpen ? <Suspense fallback={<aside className="h-full w-[280px]" />}>
            <Sidebar
              activeSessionId={activeSessionId}
              refreshKey={sidebarRefreshKey}
              onSelectSession={(id) => { setActiveSessionId(id); if (viewportWidth < 768) setLeftOpen(false); }}
              onCreateSession={() => {
                chatGenerationRef.current += 1;
                setActiveSessionId(''); setEvents([]);
                setNewSessionKey(chatGenerationRef.current);
                if (viewportWidth < 768) setLeftOpen(false);
              }}
              onDeleteSession={deleteSession}
              onLogout={() => void logout()}
              user={{ name: getDisplayName(currentUser), email: currentUser.email, imageUrl: currentUser.imageUrl }}
              ariaHidden={!leftOpen || focusChat}
              showCollapseButton={!focusChat && leftOpen}
              onCollapse={() => setLeftOpen(false)}
            />
          </Suspense> : null}
        </div>

        <main {...(drawerOpen ? { inert: '' } : {})} aria-hidden={drawerOpen || undefined} className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden transition-all duration-200 ease-in-out">
          <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-[var(--muted)]">Loading workspace...</div>}>
            <ChatInterface
              key={`${currentUser.id}:${newSessionKey}`}
              onEventsChange={(next) => { if (chatGenerationRef.current === newSessionKey) setEvents(next); }}
              onFirstStreamEvent={() => { if (viewportWidth >= 1024) setRightOpen(true); }}
              leftSidebarClosed={!leftOpen && !focusChat}
              rightSidebarClosed={!rightOpen && !focusChat}
              onOpenLeftSidebar={() => {
                if (typeof document !== 'undefined') drawerTriggerRef.current = document.querySelector('[aria-label="Mở thanh bên trái"]');
                setLeftOpen(true); if (viewportWidth < 1024) setRightOpen(false);
              }}
              onOpenRightSidebar={() => {
                if (typeof document !== 'undefined') drawerTriggerRef.current = document.querySelector('[aria-label="Mở thanh bên phải"]');
                setRightOpen(true); if (viewportWidth < 1024) setLeftOpen(false);
              }}
              focusChat={focusChat}
              selectedSessionId={activeSessionId}
              onSessionChange={(id) => { if (chatGenerationRef.current === newSessionKey) setActiveSessionId(id); }}
              onSessionActivity={() => setSidebarRefreshKey((prev) => prev + 1)}
              onToggleFocusChat={() => setFocusChat((prev) => !prev)}
            />
          </Suspense>
        </main>

        <div
          ref={rightDrawer ? drawerRef : undefined}
          tabIndex={rightDrawer ? -1 : undefined}
          role={rightDrawer ? 'dialog' : undefined}
          aria-modal={rightDrawer ? true : undefined}
          aria-label={rightDrawer ? 'Các bước phân tích' : undefined}
          className={rightDrawer ? 'fixed inset-y-0 right-0 z-50 bg-white shadow-xl' : 'relative h-full flex-shrink-0'}
          style={{
            width: !focusChat && rightOpen ? rightDrawer ? Math.min(320, viewportWidth * 0.85) : 280 : 0,
            opacity: !focusChat && rightOpen ? 1 : 0,
            overflow: !focusChat && rightOpen ? 'auto' : 'hidden',
            pointerEvents: !focusChat && rightOpen ? 'auto' : 'none',
            borderLeft: !focusChat && rightOpen ? '0.5px solid var(--line)' : '0',
            transition: 'width 220ms ease, opacity 180ms ease',
          }}
          aria-hidden={!rightOpen || focusChat}
        >
          {!focusChat && rightOpen ? <Suspense fallback={<aside className="h-full w-[280px]" />}>
            <AnalysisSidebar
              events={events}
              ariaHidden={!rightOpen || focusChat}
              showCollapseButton={!focusChat && rightOpen}
              onCollapse={() => setRightOpen(false)}
            />
          </Suspense> : null}
        </div>
      </div>
    </div>
  );
}
